"use client";

import { useState } from "react";
import { Headphones, Send } from "lucide-react";
import Header from "@/components/layout/Header";
import { useI18n } from "@/i18n/I18nProvider";

interface Message { id: string; text: string; from: "user" | "concierge"; time: string; }

function makeId() { return Date.now().toString(); }
function nowTime() { return new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }); }

const faqResponses: Record<string, string> = {
  "How do I book a service?": "You can book by going to Medical Services, selecting a service, and clicking 'Request This Service'. Our team will review and coordinate with the hospital.",
  "What documents do I need?": "Generally you'll need a valid passport, medical records (if applicable), and visa documentation. We'll provide a detailed checklist after your request is reviewed.",
  "How long does the process take?": "Initial review: 1-3 business days. Hospital coordination: 3-7 business days. Complex cases may take longer.",
  "Is my data secure?": "Yes. All medical information is encrypted and stored securely. We comply with international data protection standards.",
};

const quickQuestions = Object.keys(faqResponses);

export default function ConciergePage() {
  const { t } = useI18n();
  const [messages, setMessages] = useState<Message[]>([
    { id: "1", text: t("Welcome to ChinaCare+ Medical Concierge! How can I help you today?"), from: "concierge", time: "09:00" },
  ]);
  const [input, setInput] = useState("");
  const [typing, setTyping] = useState(false);

  const sendMessage = (text: string) => {
    if (!text.trim()) return;
    const userMsg: Message = { id: makeId(), text, from: "user", time: nowTime() };
    setMessages((prev) => [...prev, userMsg]);
    setInput("");
    setTyping(true);

    setTimeout(() => {
      const responseKey = faqResponses[text];
      const response = responseKey ? t(responseKey) : t("Thank you for your message. A concierge specialist will respond within 1 business hour. For urgent matters, please call +86-400-123-4567.");
      const conciergeMsg: Message = { id: makeId() + "1", text: response, from: "concierge", time: nowTime() };
      setMessages((prev) => [...prev, conciergeMsg]);
      setTyping(false);
    }, 1200);
  };

  return (
    <div>
      <Header />

      <div className="flex flex-col h-[calc(100dvh-160px)] min-h-[440px]">
        {/* Concierge identity bar */}
        <div className="bg-white px-5 py-3 border-b border-[#EDF1F1] flex items-center gap-3">
          <div className="w-11 h-11 rounded-full bg-primary-bg flex items-center justify-center">
            <Headphones size={21} className="text-primary" />
          </div>
          <div>
            <h1 className="text-[16px] font-bold text-text-dark leading-tight">{t("Medical Concierge")}</h1>
            <div className="flex items-center gap-1.5 mt-0.5">
              <div className="w-1.5 h-1.5 rounded-full bg-amber-500" />
              <span className="text-[12px] text-text-dark/50">{t("Demo mode · simulated replies")}</span>
            </div>
          </div>
        </div>

        <div className="bg-amber-50 border-b border-amber-200 px-4 py-2">
          <p className="text-[11px] leading-[1.5] text-amber-800">{t("Demo concierge notice")}</p>
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3 bg-page-bg">
          {messages.map((msg) => (
            <div key={msg.id} className={`flex ${msg.from === "user" ? "justify-end" : "justify-start"}`}>
              <div className={`max-w-[82%] rounded-2xl px-4 py-2.5 ${msg.from === "user" ? "bg-primary text-white rounded-br-md" : "bg-white text-text-dark border border-[#EDF1F1] rounded-bl-md"}`}>
                <p className="text-[14px] leading-[1.5]">{msg.text}</p>
                <p className={`text-[11px] mt-1 ${msg.from === "user" ? "text-white/55" : "text-text-dark/35"}`}>{msg.time}</p>
              </div>
            </div>
          ))}
          {typing && (
            <div className="flex justify-start">
              <div className="bg-white rounded-2xl rounded-bl-md px-4 py-3 border border-[#EDF1F1]">
                <div className="flex gap-1">
                  <div className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: "0ms" }} />
                  <div className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: "150ms" }} />
                  <div className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: "300ms" }} />
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Quick questions */}
        <div className="px-4 pt-2.5 pb-2 bg-page-bg">
          <div className="flex gap-2 overflow-x-auto no-scrollbar">
            {quickQuestions.map((q) => (
              <button key={q} onClick={() => sendMessage(q)}
                className="shrink-0 bg-white border border-[#E3E9E9] text-[12.5px] text-text-dark/60 px-3 py-2 rounded-full active:border-primary/40 transition-colors">
                {t(q)}
              </button>
            ))}
          </div>
        </div>

        {/* Input */}
        <div className="px-4 pb-3 pt-2 bg-white border-t border-[#EDF1F1]">
          <div className="flex items-center gap-2">
            <input value={input} onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && sendMessage(input)}
              placeholder={t("Type your question...")}
              aria-label={t("Message your concierge")}
              className="flex-1 h-11 bg-page-bg rounded-full px-4 text-[14px] text-text-dark outline-none focus:ring-1 focus:ring-primary/30 placeholder:text-text-dark/40" />
            <button onClick={() => sendMessage(input)} aria-label={t("Send message")}
              className="w-10 h-10 rounded-full bg-primary flex items-center justify-center active:bg-primary/85 transition-colors">
              <Send size={17} className="text-white" />
            </button>
          </div>
          <p className="text-[11.5px] text-text-dark/35 text-center mt-1.5">
            {t("Concierge responses are informational and do not replace medical advice.")}
          </p>
        </div>
      </div>
    </div>
  );
}
